/** @jsx jsx */
import { jsx } from "theme-ui";
import React, { useEffect } from "react";
import { Box } from "@shared-components/Box/Box";
import {
  contentContainerStyles,
  footerContainerStyles,
  heroContainerStyles,
  heroContainerFaqStyles,
  layoutContainerStyles,
  mainContainerStyles,
  heroContainerStoryStyles,
} from "./styles";
import { PrimitiveProps } from "@styles/types";
import { getThemeVariantStyles } from "@styles/utils/getThemeVariant";
import { LAYOUT_PAGE_NAV_POINT_PREFIX } from "../../constants/constants";
import { TAXONOMIES_MAP } from "../../../carlsberg-headless/taxonomies-map";

export interface PageLayoutProps extends PrimitiveProps {
  backgroundImage?: string;
  header?: React.ReactElement;
  footer?: React.ReactElement;
  hero?: React.ReactElement;
  children?: React.ReactNode;
}

const HeaderContainer: React.FC<{ children: React.ReactNode }> = (props) => (
  <Box {...props} />
);

const MainContainer: React.FC<{ children: React.ReactNode }> = (props) => (
  <Box sx={mainContainerStyles} {...props} />
);

const HeroContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const childProps = children?.valueOf() as any;
  const firstChildProps = childProps?.props.modules[0];
  const showAsFaqHero = firstChildProps?.taxonomies?.includes(
    TAXONOMIES_MAP.text_module.text_module_as_faq_hero
  );
  const showAsStoryHero = firstChildProps?.taxonomies?.includes(
    TAXONOMIES_MAP.general.show_as_story_hero
  );
  return (
    <Box
      as="header"
      sx={{
        ...heroContainerStyles,
        ...(showAsFaqHero && { ...heroContainerFaqStyles }),
        ...(showAsStoryHero && { ...heroContainerStoryStyles }),
      }}
    >
      {children}
    </Box>
  );
};

const ContentContainer: React.FC<{ children: React.ReactNode; sx?: any }> = (
  props
) => (
  <Box
    as="main"
    id={LAYOUT_PAGE_NAV_POINT_PREFIX}
    sx={contentContainerStyles}
    {...props}
  />
);

const FooterContainer: React.FC<{ children: React.ReactNode }> = (props) => (
  <Box as="footer" sx={footerContainerStyles} {...props} />
);

const PageLayout: React.FC<PageLayoutProps> = ({
  backgroundImage,
  header,
  footer,
  hero,
  children,
  sx,
}) => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) {
      const targetId = window.location.hash.slice(1);
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "instant" });
      }
    }
  }, []);

  return (
    <Box
      sx={{
        ...layoutContainerStyles,
        ...sx,
      }}
    >
      {header && <HeaderContainer>{header}</HeaderContainer>}
      <MainContainer>
        {hero && <HeroContainer>{hero}</HeroContainer>}
        <ContentContainer
          sx={{
            ":before, :after": {
              content: "' '",
              display: "table",
            },
            ...(backgroundImage && {
              backgroundImage: `url(${backgroundImage})`,
            }),
            ...getThemeVariantStyles("layoutContainer", "default"),
          }}
        >
          {children}
        </ContentContainer>
      </MainContainer>
      {footer && <FooterContainer>{footer}</FooterContainer>}
      <div id="navigation" />
      <Box id="beerDetail" sx={{ position: "absolute", zIndex: "39" }} />
      <Box id="EntryModule" sx={{ position: "absolute", zIndex: "50" }} />
    </Box>
  );
};

export { PageLayout };
